import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AdminUserSkuRequest,
  ListResponseOfUserSkuExtended,
  UserSkuExtended,
  UserSkuFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";

type SelectParentUserSkuProps = {
  organizationId: string;
  userId: string;
  currentParentUserSkuId?: string | null;
  onChange: (id: string | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<UserSkuExtended>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectParentUserSku = ({
  organizationId,
  userId,
  currentParentUserSkuId,
  onChange,
  ...rest
}: SelectParentUserSkuProps) => {
  const { formatMessage: t } = useIntl();
  const { userSkuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [parentUserSku, setParentUserSku] = useState<UserSkuExtended | null>(
    null
  );
  const { organizationId: contextOrgId } = contextStore.useState();

  const fetchUserSkus = async (searchTerm: string) => {
    const request = new AdminUserSkuRequest({
      organizationId: organizationId ?? contextOrgId,
      userId: userId,
      pageNumber: 0,
      pageSize: 20,
      sortField: "sku",
      parameters: new UserSkuFilter({
        searchText: searchTerm === "" ? undefined : searchTerm,
        excludedIds: currentParentUserSkuId ? [currentParentUserSkuId] : [],
      }),
    });

    const result = await queryClient.fetchQuery<ListResponseOfUserSkuExtended>(
      ["skus", request],
      () => userSkuService.getParentUserSkus(request)
    );

    return { result, forSearchTerm: searchTerm };
  };

  const onChangeUserSku = (parentUserSku: UserSkuExtended | null) => {
    setParentUserSku(parentUserSku);
    onChange(parentUserSku?.id ?? null);
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={parentUserSku}
      labelField="sku"
      idField="id"
      fetchData={fetchUserSkus}
      failMessage={t({ id: "selectParentUserSkuFetchFailed" })}
      onChange={onChangeUserSku}
    />
  );
};

export default SelectParentUserSku;
